// BM-H595 (23 Sep 2026): everything the editor says about the draft versus
// the FORF, in one strip above the letter:
//   - "Checking the draft against the FORF" while the automatic AI check runs
//   - the amber notice when that check did not run
//   - the banner when the FORF has changed since the draft was built, with
//     "Rebuild from FORF" (the current draft goes into version history first)
//   - the list of changes the check made, each with Undo, plus Undo all
//   - the checker's findings (money, names, scope) against the FORF
import { Loader2, RotateCcw, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const FIELD_LABELS = {
  fee_amount: "Fee",
  disbursements: "Disbursements",
  poa_amount: "Payment on account",
  poa_required: "Payment on account",
  pricing_model: "Charging basis",
  scope_exclusions: "Scope exclusions",
  scope_exclusions_list: "Scope exclusions",
  timescale: "Timescale",
  fee_earner: "Fee earner",
  supervisor: "Supervisor",
  client_name: "Client name",
  client_address: "Client address",
  disbursement_account: "Disbursements on account",
};

export default function CclForfCheck({
  running,
  status,
  reason,
  changes = [],
  findings = [],
  forfChanged,
  onRebuild,
  rebuilding,
  onUndoChange,
  onUndoAll,
  undoingAll,
  onUpdateAddress,
  updatingAddress,
  disabled,
}) {
  const shownFindings = (findings || []).filter((f) => f.severity === "high" || f.severity === "medium");
  if (!running && status !== "failed" && !forfChanged && changes.length === 0 && shownFindings.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2 border-b border-slate-200 bg-white px-4 py-3 text-sm" data-testid="ccl-forf-check">
      {running && (
        <div className="flex items-center gap-2 text-slate-700" data-testid="ccl-presweep-running" role="status">
          <Loader2 className="h-4 w-4 animate-spin text-teal" />
          <span>Checking the draft against the FORF</span>
          <span className="text-xs text-slate-400">Editing unlocks when the check finishes</span>
        </div>
      )}

      {!running && status === "failed" && (
        <div
          className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900"
          data-testid="ccl-presweep-failed"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The AI check against the FORF did not run ({reason || "no reason given"}). The draft below is built
            directly from the FORF. Run the once-over when ready.
          </span>
        </div>
      )}

      {forfChanged && (
        <div
          className="flex flex-wrap items-center gap-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900"
          data-testid="ccl-forf-changed-banner"
          role="alert"
        >
          <span className="font-medium">The FORF has changed since this draft was built</span>
          <Button
            size="sm"
            variant="outline"
            onClick={onRebuild}
            disabled={rebuilding || disabled}
            className="gap-1.5"
            data-testid="ccl-rebuild-from-forf-btn"
            title="Your current draft is kept in the version history before the letter is rebuilt"
          >
            {rebuilding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Rebuild from FORF
          </Button>
        </div>
      )}

      {changes.length > 0 && (
        <div className="rounded border border-slate-200 px-3 py-2" data-testid="ccl-presweep-changes">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="font-medium text-slate-800">
              {/* 24 Sep 2026: the first-draft check also polishes wording from the
                  matter record (hub.ccl_presweep kind "polish"), so the heading
                  names both. */}
              Changed in the first draft ({changes.length})
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={onUndoAll}
              disabled={undoingAll || disabled}
              className="gap-1.5"
              data-testid="ccl-presweep-undo-all"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Undo all
            </Button>
          </div>
          <ul className="space-y-1.5">
            {changes.map((c) => (
              <li key={c.id} className="flex items-start justify-between gap-3" data-testid="ccl-presweep-change">
                <span className="text-slate-700">
                  <span className="font-medium">
                    {c.kind === "polish" ? "Wording polished" : (FIELD_LABELS[c.forf_field] || "Wording")}:
                  </span>{" "}
                  <span className="line-through text-slate-400">{c.before}</span>{" "}
                  <span className="text-slate-900">{c.after}</span>
                  {c.reason ? <span className="block text-xs text-slate-500">{c.reason}</span> : null}
                </span>
                <button
                  type="button"
                  onClick={() => onUndoChange?.(c)}
                  disabled={disabled}
                  className="shrink-0 text-xs text-teal underline disabled:opacity-50"
                  data-testid={`ccl-presweep-undo-${c.id}`}
                >
                  Undo
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {shownFindings.length > 0 && (
        <div className="rounded border border-slate-200 px-3 py-2" data-testid="ccl-forf-findings">
          <span className="font-medium text-slate-800">Checks against the FORF</span>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-slate-700">
            {shownFindings.map((f, i) => (
              <li key={`${f.code}-${i}`} data-testid="ccl-forf-finding">
                {f.message}
                {f.blocks_review ? <span className="ml-1 text-xs text-red-700">(must be fixed before review)</span> : null}
                {/* BM-H3747 (23 Oct 2026): put the FORF address in without a
                    rebuild, so agreed edits are kept. */}
                {f.code === "address_missing" && onUpdateAddress ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onUpdateAddress}
                    disabled={updatingAddress || disabled}
                    className="ml-2 h-7 gap-1.5"
                    data-testid="ccl-update-address-btn"
                    title="Puts the FORF address under the client's name and saves a new version. Nothing else in the letter changes."
                  >
                    {updatingAddress ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Update address
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
